import { track } from '@eventbrite/datalayer-library';
import {
    CardSize,
    GoodToKnow as G2K,
    TrackingProvider,
    TranslationProvider,
} from '@eventbrite/good-to-know';
import { logEvent } from '@eventbrite/statsig';
import React, { useMemo } from 'react';
import { LISTING_HEAP_IDS } from '../../constants';
import { useEventBasicInformationContext } from '../../contexts';
import { STATSIG_EVENT_NAMES } from '../../experimentation';
import { RefundPolicyProps } from '../../types';
import { type HighlightsProps } from '../../types/highlights';
import { EventDetailsMainInner } from '../EventDetails/EventDetails';
import { type LocationProps } from '../EventDetails/WhenAndWhere/Location/Location';
import { getTranslations } from './translations';
import { formatDuration, getTranslatedRefundPolicy } from './utils';

type Props = {
    highlights: HighlightsProps;
    refundPolicy: RefundPolicyProps;
    isOnline?: boolean;
    doorTime?: string;
    ageRestriction?: string;
    location?: LocationProps;
};

type G2KHighlights = React.ComponentProps<typeof G2K>['highlights'];

export const GoodToKnow: React.FC<Props> = ({
    highlights,
    isOnline,
    doorTime,
    ageRestriction,
    refundPolicy,
    location,
}) => {
    const g2kRef = React.useRef<HTMLDivElement>(null);
    const eventContext = useEventBasicInformationContext();
    const isRepeating = eventContext?.isRepeating || false;

    const dHighlights: G2KHighlights = useMemo((): G2KHighlights => {
        return {
            ...highlights,
            isOnline,
            doorTime,
            age_restriction: ageRestriction,
            durationString: formatDuration(highlights.duration),
            duration: undefined,
        };
    }, [highlights, isOnline, doorTime, ageRestriction]);

    const translatedRefundPolicy = getTranslatedRefundPolicy(
        refundPolicy,
        isRepeating,
    );

    return (
        <EventDetailsMainInner>
            <TranslationProvider translations={getTranslations(location)}>
                <TrackingProvider
                    data-testid="g2k-container"
                    onSectionVisible={(sectionKey: string) => {
                        track({
                            eventName: LISTING_HEAP_IDS.G2K_VIEW_CARD,
                            eventData: {
                                card_key: sectionKey,
                            },
                        });
                        logEvent(STATSIG_EVENT_NAMES.G2K_VIEW_CARD, null, {
                            card_key: sectionKey,
                        });
                    }}
                    onReadMoreClick={(sectionKey: string) => {
                        track({
                            eventName: LISTING_HEAP_IDS.G2K_READ_MORE_CLICK,
                            eventData: {
                                card_key: sectionKey,
                            },
                        });
                        logEvent(
                            STATSIG_EVENT_NAMES.G2K_READ_MORE_CLICK,
                            null,
                            {
                                card_key: sectionKey,
                            },
                        );
                    }}
                >
                    <G2K
                        highlights={dHighlights}
                        refundPolicy={translatedRefundPolicy || ''}
                        cardSize={
                            refundPolicy
                                ? CardSize.STRETCHED
                                : CardSize.CONTAINED
                        }
                        showViewAll={false}
                    />
                </TrackingProvider>
            </TranslationProvider>
            <div ref={g2kRef}></div>
        </EventDetailsMainInner>
    );
};
